// src/components/SalesDashboard/SalesOrderDetail.js
import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronLeft, ChevronDown, ChevronUp, CheckCircle, XCircle,
  RefreshCw, Loader, AlertTriangle, Clock, Zap, FileText, Package,
} from 'lucide-react';
import api from '../api';

const S = `
  .sod-wrap { max-width: 1100px; margin: 0 auto; font-family: 'Inter', sans-serif; color: #000; }
  .sod-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 12px; }
  .sod-header-left h2 { font-size: 22px; font-weight: 800; color: #1e293b; margin: 0 0 6px; }

  .sod-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 700; white-space: nowrap; }

  .sod-grid { display: grid; grid-template-columns: 300px 1fr; gap: 20px; align-items: start; }
  @media(max-width:800px) { .sod-grid { grid-template-columns: 1fr; } }

  .sod-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 22px; margin-bottom: 18px; }
  .sod-card-title { font-size: 13px; font-weight: 700; color: #000; margin: 0 0 14px; display: flex; align-items: center; gap: 7px; }
  .sod-row { display: flex; justify-content: space-between; padding: 7px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
  .sod-row:last-child { border-bottom: none; }
  .sod-label { color: #1a1a1a; font-weight: 600; }
  .sod-val { color: #1e293b; font-weight: 600; text-align: right; }

  .sod-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .sod-table thead tr { background: #f8fafc; }
  .sod-table th { padding: 10px 14px; text-align: left; font-size: 11px; font-weight: 700; color: #1a1a1a; text-transform: uppercase; letter-spacing: .4px; border-bottom: 2px solid #e2e8f0; }
  .sod-table td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; color: #000; }
  .sod-table tbody tr:last-child td { border-bottom: none; }

  .sod-progress-bar { height: 6px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
  .sod-progress-fill { height: 100%; border-radius: 4px; background: linear-gradient(90deg,#6366f1,#8b5cf6); transition: width .3s; }

  .sod-btn {
    display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px;
    border-radius: 10px; font-size: 13px; font-weight: 700; cursor: pointer;
    border: none; transition: opacity .15s; white-space: nowrap;
  }
  .sod-btn:hover { opacity: .88; }
  .sod-btn:disabled { opacity: .5; cursor: not-allowed; }
  .sod-btn-primary  { background: linear-gradient(135deg,#1e1b4b,#312e81); color: white; }
  .sod-btn-success  { background: linear-gradient(135deg,#10b981,#059669); color: white; }
  .sod-btn-danger   { background: linear-gradient(135deg,#ef4444,#dc2626); color: white; }
  .sod-btn-warning  { background: linear-gradient(135deg,#f59e0b,#d97706); color: white; }
  .sod-btn-outline  { background: white; color: #1a1a1a; border: 1.5px solid #e2e8f0; }
  .sod-actions { display: flex; gap: 10px; flex-wrap: wrap; }

  .sod-version-snap { background: #f8fafc; border-radius: 10px; padding: 14px; margin-top: 8px; font-size: 12px; }
  .sod-version-snap table { width: 100%; border-collapse: collapse; }
  .sod-version-snap td, .sod-version-snap th { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; }
  .sod-version-snap th { color: #1a1a1a; font-weight: 600; }

  .sod-prog-bar-full { height: 10px; background: #e2e8f0; border-radius: 6px; overflow: hidden; margin: 5px 0; }
  .sod-prog-fill { height: 100%; border-radius: 6px; transition: width .4s; }

  .sod-cert-btn { display: inline-flex; align-items: center; gap: 5px; margin-top: 6px; padding: 4px 10px; border-radius: 7px; font-size: 11px; font-weight: 700; background: #f0fdf4; border: 1.5px solid #86efac; color: #15803d; cursor: pointer; }
  .sod-cert-btn:hover { background: #dcfce7; }

  .sod-tl-wrap { display: flex; align-items: flex-start; padding: 20px 0 4px; }
  .sod-tl-step { flex: 1; display: flex; flex-direction: column; align-items: center; position: relative; }
  .sod-tl-step:not(:last-child)::after { content: ''; position: absolute; top: 7px; left: 50%; width: 100%; height: 2px; z-index: 0; }
  .sod-tl-dot { width: 16px; height: 16px; border-radius: 50%; z-index: 1; border: 2px solid white; flex-shrink: 0; }
  .sod-tl-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; margin-top: 6px; text-align: center; }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

const STATUS_CONFIG = {
  CONFIRMED:     { bg: '#dbeafe', color: '#1d4ed8' },
  SENT_TO_PRODUCTION: { bg: '#ede9fe', color: '#6d28d9' },
  IN_PRODUCTION: { bg: '#fef3c7', color: '#b45309' },
  COMPLETED:     { bg: '#d1fae5', color: '#065f46' },
  INVOICED:      { bg: '#ede9fe', color: '#6d28d9' },
  CANCELLED:     { bg: '#fee2e2', color: '#b91c1c' },
};

const PRIORITY_LABELS = {
  1: 'Urgent',
  2: 'High',
  3: 'Normal',
  4: 'Low',
  5: 'Very Low',
};

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const downloadBlobFile = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { bg: '#f1f5f9', color: '#64748b' };
  return <span className="sod-badge" style={{ background: cfg.bg, color: cfg.color }}>{status?.replace('_', ' ')}</span>;
};

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
    <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
  </div>
);

function VersionAccordion({ v }) {
  const [open, setOpen] = useState(false);
  const snap = v.snapshot || {};
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 10, marginBottom: 10 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}
        onClick={() => setOpen(o => !o)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, color: '#6366f1', fontSize: 13 }}>V{v.version_no}</span>
          <span style={{ fontSize: 12, color: '#1a1a1a' }}>{v.change_summary}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
          {open ? <ChevronUp size={14} color="#94a3b8" /> : <ChevronDown size={14} color="#94a3b8" />}
        </div>
      </div>
      {open && snap.items && (
        <div className="sod-version-snap">
          <table>
            <thead>
              <tr><th>Design</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr>
            </thead>
            <tbody>
              {snap.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.design_code} {it.design_name && `— ${it.design_name}`}</td>
                  <td>{it.qty}</td>
                  <td>₹{Number(it.price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td style={{ fontWeight: 600 }}>₹{Number(it.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 8, fontSize: 12, color: '#1a1a1a' }}>
            Order Total: <strong>₹{Number(snap.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
          </div>
        </div>
      )}
    </div>
  );
}

const ITEM_STATUS_CFG = {
  PENDING:       { bg: '#f1f5f9', color: '#475569' },
  SENT_TO_PRODUCTION: { bg: '#ede9fe', color: '#6d28d9' },
  IN_PRODUCTION: { bg: '#fef3c7', color: '#b45309' },
  PACKING:       { bg: '#e0f2fe', color: '#0369a1' },
  COMPLETED:     { bg: '#d1fae5', color: '#065f46' },
};

const STAGE_STATUS_CFG = {
  QC_APPROVED: { bg: '#d1fae5', color: '#065f46', label: 'Approved' },
  IN_PROGRESS: { bg: '#dbeafe', color: '#1d4ed8', label: 'In Progress' },
  LOCKED: { bg: '#f1f5f9', color: '#64748b', label: 'Locked' },
  PENDING: { bg: '#f8fafc', color: '#475569', label: 'Pending' },
  QC_PENDING: { bg: '#ffedd5', color: '#c2410c', label: 'QC Pending' },
};

function ItemProgressCard({ item, onDownloadCertificate, downloadingCertificateId }) {
  const prog = item.job_card_progress;
  const isInventoryReady = prog?.job_card_status === 'COMPLETED' && prog?.delivery_status === 'READY_FOR_DISPATCH';
  const isDone = isInventoryReady || item.status === 'COMPLETED';
  const fillColor = isDone ? '#10b981' : '#6366f1';
  const statusKey = prog?.job_card_status === 'PACKING' ? 'PACKING' : item.status;
  const sc = ITEM_STATUS_CFG[statusKey] || { bg: '#f1f5f9', color: '#64748b' };
  const approvedStageNotes = Array.isArray(prog?.approved_stage_notes) ? prog.approved_stage_notes : [];

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>{item.design_code}</span>
          {item.design_name && (
            <span style={{ color: '#1a1a1a', fontSize: 12, marginLeft: 8 }}>{item.design_name}</span>
          )}
          <span style={{ color: '#1a1a1a', fontSize: 12, marginLeft: 6 }}>× {item.quantity} pcs</span>
        </div>
        <span className="sod-badge" style={{ background: sc.bg, color: sc.color, fontSize: 11 }}>
          {prog?.status_label || item.status}
        </span>
      </div>

      {prog ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#1a1a1a', marginBottom: 2 }}>
            <span>Production Progress</span>
            <span>{prog.stages_done}/{prog.stages_total} stages — {prog.pct}%</span>
          </div>
          <div className="sod-prog-bar-full">
            <div
              className="sod-prog-fill"
              style={{ width: `${prog.pct}%`, background: `linear-gradient(90deg, ${fillColor}, ${isDone ? '#059669' : '#8b5cf6'})` }}
            />
          </div>
          <div style={{ fontSize: 11, color: '#1a1a1a', marginTop: 3 }}>
            Job Card: <span style={{ fontFamily: 'monospace', color: '#4f46e5', fontWeight: 600 }}>{prog.job_card_no}</span>
            {prog.current_stage && (
              <span style={{ marginLeft: 8 }}>· Currently: <b style={{ color: '#1e293b' }}>{prog.current_stage}</b></span>
            )}
          </div>
          {prog?.job_card_status === 'PACKING' && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#0369a1', fontWeight: 700 }}>
              Status: In Packing
            </div>
          )}
          {isInventoryReady && (
            <div style={{ marginTop: 6, fontSize: 11, color: '#15803d', fontWeight: 700 }}>
              Status: Ready for Dispatch (In Inventory)
            </div>
          )}
          {approvedStageNotes.length > 0 && (
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {approvedStageNotes.map((note) => (
                <span
                  key={`${note.stage_name}-${note.open_rework_pieces}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 8px',
                    borderRadius: 999,
                    background: '#fff7ed',
                    border: '1px solid #fdba74',
                    color: '#c2410c',
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {note.stage_name}: {note.label}
                </span>
              ))}
            </div>
          )}
          {isDone && item.job_card_id && (
            <button
              className="sod-cert-btn"
              onClick={() => onDownloadCertificate(item.job_card_id)}
              disabled={downloadingCertificateId === item.job_card_id}
            >
              <CheckCircle size={11} /> {downloadingCertificateId === item.job_card_id ? 'Downloading...' : 'Download QC Certificate'}
            </button>
          )}
        </>
      ) : (
        <div style={{ fontSize: 12, color: item.status === 'SENT_TO_PRODUCTION' ? '#6d28d9' : '#f59e0b', marginTop: 4 }}>
          {item.status === 'SENT_TO_PRODUCTION' ? 'Sent to Production inbox and awaiting manager review' : 'Awaiting production start'}
        </div>
      )}
    </div>
  );
}

const TL_STEPS = ['CONFIRMED', 'SENT_TO_PRODUCTION', 'IN_PRODUCTION', 'COMPLETED', 'INVOICED'];

function OrderStatusTimeline({ status }) {
  const currentIdx = TL_STEPS.indexOf(status);
  return (
    <div className="sod-tl-wrap">
      {TL_STEPS.map((step, idx) => {
        const done = idx <= currentIdx;
        const active = idx === currentIdx;
        const dotColor = done ? (active ? '#6366f1' : '#10b981') : '#e2e8f0';
        const lineColor = idx < currentIdx ? '#10b981' : '#e2e8f0';
        return (
          <div key={step} className="sod-tl-step" style={{ '--line-color': lineColor }}>
            <div
              className="sod-tl-dot"
              style={{
                background: dotColor,
                boxShadow: active ? `0 0 0 3px ${dotColor}33` : 'none',
              }}
            />
            {/* connector line to the right */}
            {idx < TL_STEPS.length - 1 && (
              <div style={{
                position: 'absolute', top: 7, left: '50%', width: '100%', height: 2,
                background: lineColor, zIndex: 0,
              }} />
            )}
            <div
              className="sod-tl-label"
              style={{ color: active ? '#6366f1' : done ? '#15803d' : '#94a3b8', fontSize: 10 }}
            >
              {step.replace('_', ' ')}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StageBadge({ status }) {
  const cfg = STAGE_STATUS_CFG[status] || { bg: '#f1f5f9', color: '#475569', label: status };
  return (
    <span className="sod-badge" style={{ background: cfg.bg, color: cfg.color, fontSize: 11 }}>
      {cfg.label}
    </span>
  );
}

function ProductionProgressPanel({ progress, loading }) {
  if (loading) {
    return (
      <div className="sod-card">
        <div className="sod-card-title"><Package size={15} color="#6366f1" /> Production Progress</div>
        <div style={{ fontSize: 13, color: '#1a1a1a' }}>Loading production progress...</div>
      </div>
    );
  }

  if (!progress) return null;

  return (
    <div className="sod-card">
      <div className="sod-card-title"><Package size={15} color="#6366f1" /> Production Progress</div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>{progress.current_status}</div>
          <div style={{ fontSize: 12, color: '#1a1a1a' }}>{progress.completion_pct}% complete</div>
        </div>
        <div className="sod-prog-bar-full">
          <div className="sod-prog-fill" style={{ width: `${progress.completion_pct}%`, background: 'linear-gradient(90deg,#2563eb,#10b981)' }} />
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: '#1a1a1a', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <span>Job Cards: <b>{progress.job_card_count}</b></span>
          {progress.is_in_packing ? <span style={{ color: '#0369a1', fontWeight: 700 }}>In Packing</span> : null}
          {progress.is_in_inventory ? <span style={{ color: '#15803d', fontWeight: 700 }}>Ready for Dispatch (In Inventory)</span> : null}
          {progress.rework_pieces_count > 0 ? <span style={{ color: '#c2410c', fontWeight: 700 }}>{progress.rework_pieces_count} pieces in rework queue</span> : null}
        </div>
      </div>

      {progress.job_cards.length === 0 ? (
        <div style={{ fontSize: 12, color: '#6d28d9' }}>This order has been sent to production, but job cards have not been created yet.</div>
      ) : (
        progress.job_cards.map((jobCard) => (
          <div key={jobCard.job_card_id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontWeight: 700, color: '#4f46e5', fontSize: 13 }}>{jobCard.job_card_no}</div>
                <div style={{ fontSize: 12, color: '#1a1a1a', marginTop: 2 }}>{jobCard.status_label}</div>
              </div>
              <div style={{ fontSize: 12, color: '#1a1a1a' }}>{jobCard.completion_pct}% complete</div>
            </div>
            <div className="sod-prog-bar-full" style={{ marginBottom: 10 }}>
              <div className="sod-prog-fill" style={{ width: `${jobCard.completion_pct}%`, background: 'linear-gradient(90deg,#6366f1,#10b981)' }} />
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {jobCard.stages.map((stage) => (
                <div key={stage.stage_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', padding: '8px 10px', borderRadius: 8, background: '#f8fafc', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e293b' }}>{stage.stage_name}</div>
                    <div style={{ fontSize: 11, color: '#475569' }}>
                      {stage.worker_name ? `Worker: ${stage.worker_name}` : 'Worker not assigned'}
                      {stage.completed_at ? ` • ${new Date(stage.completed_at).toLocaleString()}` : ''}
                    </div>
                  </div>
                  <StageBadge status={stage.status} />
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default function SalesOrderDetail({ orderId, onBack }) {
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [versions, setVersions] = useState([]);
  const [productionProgress, setProductionProgress] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [downloadingCertificateId, setDownloadingCertificateId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [oldStockSuggestion, setOldStockSuggestion] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setProgressLoading(true);
    setError('');
    try {
      const [orderRes, progressRes] = await Promise.all([
        api.get(`/sales/orders/${orderId}`),
        api.get(`/sales/orders/${orderId}/production-progress`),
      ]);
      setOrder(orderRes.data.order);
      setItems(orderRes.data.items);
      setVersions(orderRes.data.versions || []);
      setProductionProgress(progressRes.data?.data || null);
      try {
        const suggestionRes = await api.get(`/sales/store-inventory/old-stock/suggest-for-order/${orderId}`);
        setOldStockSuggestion(suggestionRes.data || null);
      } catch {
        setOldStockSuggestion(null);
      }
    } catch {
      setError('Failed to load order.');
      setProductionProgress(null);
      setOldStockSuggestion(null);
    }
    setLoading(false);
    setProgressLoading(false);
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (fn, successMsg) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      await fn();
      setSuccess(successMsg);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Action failed.');
    }
    setActionLoading(false);
  };

  const downloadCertificate = useCallback(async (jobCardId) => {
    if (!jobCardId) return;
    setDownloadingCertificateId(jobCardId);
    setError('');
    try {
      const response = await api.get(`/final-qc/${jobCardId}/certificate`, { responseType: 'blob' });
      downloadBlobFile(response.data, `QC_Certificate_JC-${jobCardId}.pdf`);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to download QC certificate.');
    } finally {
      setDownloadingCertificateId(null);
    }
  }, []);

  if (loading) return <><style>{S}</style><Spinner /></>;
  if (!order) return <><style>{S}</style><div style={{ color: '#dc2626', padding: 24 }}>Order not found.</div></>;

  const deliveryDate = order.expected_delivery && order.expected_delivery !== 'None'
    ? new Date(order.expected_delivery)
    : null;

  const hasJobCards = items.some(i => i.job_card_id);
  const suggestedItems = Array.isArray(oldStockSuggestion?.items) ? oldStockSuggestion.items.filter((item) => item.old_stock_available > 0) : [];

  return (
    <>
      <style>{S}</style>

      <div className="sod-wrap">
        {/* Back */}
        <button
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#6366f1', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 20, padding: 0 }}
          onClick={onBack}
        >
          <ChevronLeft size={16} /> Back to Orders
        </button>

        {/* Header */}
        <div className="sod-header">
          <div className="sod-header-left">
            <h2>{order.order_code}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <StatusBadge status={order.status} />
              <span style={{ fontSize: 13, color: '#1a1a1a' }}>{order.customer_name}</span>
              <span style={{ fontSize: 12, color: '#1a1a1a' }}>V{order.version}</span>
            </div>
          </div>
          <button className="sod-btn sod-btn-outline" onClick={load} disabled={loading}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 10, padding: '12px 16px', color: '#dc2626', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}
        {success && (
          <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '12px 16px', color: '#15803d', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle size={16} /> {success}
          </div>
        )}

        {suggestedItems.length > 0 && (
          <div style={{ background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', border: '1.5px solid #93c5fd', borderRadius: 14, padding: '14px 18px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontWeight: 800, color: '#1d4ed8', fontSize: 15 }}>Old stock available for this order</div>
                <div style={{ fontSize: 13, color: '#1e3a8a', marginTop: 4 }}>
                  {suggestedItems.map((item) => `${item.old_stock_available} pcs of ${item.design_code}`).join(' | ')}
                </div>
              </div>
              <button
                className="sod-btn sod-btn-outline"
                onClick={() => window.alert(`Matching stock found for: ${suggestedItems.map((item) => item.design_code).join(', ')}. Open Inventory > Old Stock to review matching batches.`)}
              >
                <Package size={15} /> View Matching Stock
              </button>
            </div>
          </div>
        )}

        {/* Production complete banner */}
        {order.status === 'COMPLETED' && (
          <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1.5px solid #86efac', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <CheckCircle size={22} color="#15803d" />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, color: '#14532d', fontSize: 15 }}>
                  Production Complete — Ready for Delivery &amp; Invoicing
                </div>
                <div style={{ fontSize: 13, color: '#166534', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#bbf7d0', color: '#14532d', padding: '2px 10px', borderRadius: 10, fontWeight: 700, fontSize: 12 }}>
                    <Package size={12} /> Product added to Store Inventory
                  </span>
                  {order.balance_due > 0 && (
                    <span style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>
                      Balance Due: ₹{Number(order.balance_due).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="sod-btn sod-btn-success"
                style={{ fontSize: 14, padding: '12px 24px' }}
                disabled={actionLoading}
                onClick={() => doAction(
                  () => api.post(`/sales/orders/${orderId}/mark-invoiced`),
                  'Order marked as Invoiced — proceed to generate the physical invoice.'
                )}
              >
                {actionLoading ? <Loader size={16} /> : <FileText size={16} />}
                Generate Invoice
              </button>
            </div>
          </div>
        )}

        <div className="sod-grid">
          {/* Left column */}
          <div>
            {/* Order info */}
            <div className="sod-card">
              <div className="sod-card-title"><FileText size={15} color="#6366f1" /> Order Info</div>
              <div className="sod-row">
                <span className="sod-label">Customer</span>
                <span className="sod-val">{order.customer_name}</span>
              </div>
              {order.customer_phone && (
                <div className="sod-row">
                  <span className="sod-label">Phone</span>
                  <span className="sod-val">{order.customer_phone}</span>
                </div>
              )}
              <div className="sod-row">
                <span className="sod-label">Priority</span>
                <span className="sod-val">{PRIORITY_LABELS[order.priority] || 'Normal'}</span>
              </div>
              <div className="sod-row">
                <span className="sod-label">Delivery Date</span>
                <span className="sod-val">
                  {deliveryDate ? deliveryDate.toLocaleDateString() : '—'}
                </span>
              </div>
              {order.special_instructions && (
                <div className="sod-row">
                  <span className="sod-label">Instructions</span>
                  <span className="sod-val" style={{ maxWidth: 160, textAlign: 'right', fontSize: 12 }}>
                    {order.special_instructions}
                  </span>
                </div>
              )}
              {order.quotation_id && (
                <div className="sod-row">
                  <span className="sod-label">Source</span>
                  <span className="sod-val" style={{ color: '#6366f1', fontSize: 12 }}>From Quotation #{order.quotation_id}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="sod-card">
              <div className="sod-card-title">Actions</div>
              <div className="sod-actions">
                {order.status === 'CONFIRMED' && !hasJobCards && (
                  <button
                    className="sod-btn sod-btn-primary"
                    disabled={actionLoading}
                    onClick={() => {
                      if (!window.confirm('This will send the order to the Production team. No job cards will be created until the Production Manager reviews and approves.')) {
                        return;
                      }
                      doAction(
                        () => api.post(`/sales/orders/${orderId}/send-to-production`),
                        'Order sent to Production. No job cards were created.'
                      );
                    }}
                  >
                    {actionLoading ? <Loader size={14} /> : <Zap size={14} />}
                    Send to Production
                  </button>
                )}
                {order.status === 'CONFIRMED' && hasJobCards && (
                  <div style={{ fontSize: 13, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={14} /> Job cards already created.
                  </div>
                )}
                {order.status === 'SENT_TO_PRODUCTION' && (
                  <div style={{ fontSize: 13, color: '#6d28d9', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Clock size={14} /> Awaiting Production Manager review and job card creation.
                  </div>
                )}
                {order.status === 'IN_PRODUCTION' && (
                  <>
                    <div style={{ fontSize: 13, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Clock size={14} /> Order is in production — track via Production module.
                    </div>
                    <button
                      className="sod-btn sod-btn-success"
                      disabled={actionLoading}
                      onClick={() => doAction(
                        () => api.post(`/sales/orders/${orderId}/mark-completed`),
                        'Order marked as Completed.'
                      )}
                    >
                      {actionLoading ? <Loader size={14} /> : <CheckCircle size={14} />}
                      Mark Completed
                    </button>
                  </>
                )}
                {order.status === 'COMPLETED' && (
                  <div style={{ fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={14} /> Use the Generate Invoice button above.
                  </div>
                )}
                {order.status === 'INVOICED' && (
                  <div style={{ fontSize: 13, color: '#6d28d9', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle size={14} /> Order invoiced — process payment in Accounts module.
                  </div>
                )}
                {!['COMPLETED', 'INVOICED', 'CANCELLED'].includes(order.status) && (
                  <button
                    className="sod-btn sod-btn-danger"
                    disabled={actionLoading}
                    onClick={() => {
                      if (window.confirm('Cancel this order? This cannot be undone.')) {
                        doAction(
                          () => api.post(`/sales/orders/${orderId}/cancel`),
                          'Order cancelled.'
                        );
                      }
                    }}
                  >
                    <XCircle size={14} /> Cancel Order
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div>
            <ProductionProgressPanel progress={productionProgress} loading={progressLoading} />

            {/* Items with production progress */}
            <div className="sod-card">
              <div className="sod-card-title">
                <FileText size={15} color="#6366f1" /> Order Items ({items.length})
              </div>

              {/* Financials summary row */}
              <div style={{ display: 'flex', gap: 24, fontSize: 12, color: '#1a1a1a', marginBottom: 14, flexWrap: 'wrap' }}>
                <span>Total: <b style={{ color: '#1e1b4b' }}>₹{fmt(order.total_amount)}</b></span>
                <span>Advance: <b style={{ color: '#10b981' }}>₹{fmt(order.advance_received)}</b></span>
                <span>Balance: <b style={{ color: order.balance_due > 0 ? '#dc2626' : '#64748b' }}>₹{fmt(order.balance_due)}</b></span>
              </div>

              {items.map(it => (
                <ItemProgressCard
                  key={it.item_id}
                  item={it}
                  onDownloadCertificate={downloadCertificate}
                  downloadingCertificateId={downloadingCertificateId}
                />
              ))}

              {/* Order status timeline */}
              <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>
                  Order Timeline
                </div>
                <OrderStatusTimeline status={order.status} />
              </div>
            </div>

            {/* Version History */}
            {versions.length > 0 && (
              <div className="sod-card">
                <div className="sod-card-title"><Clock size={15} color="#6366f1" /> Version History</div>
                {versions.map(v => <VersionAccordion key={v.version_no} v={v} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
